import { createContext, type ReactNode, useCallback, useContext } from 'react';
import Constant from 'lodash/constant';
import { useSettingsByPath } from 'pos-client/hooks/useSettings.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { NavigationPermissionModel } from 'pos-shared/models/navigationPermissionSchema.ts';
import { time } from 'pos-shared/utils/secondsHelper.ts';

type NavigationPermissionProps = {
  navigationPermissions: NavigationPermissionModel | undefined;
  isNavigationPermissionsLoading: boolean;
  hasAccessToRoute: (route: string) => boolean;
};

const NavigationPermissionContext = createContext<NavigationPermissionProps>({
  navigationPermissions: [],
  isNavigationPermissionsLoading: false,
  hasAccessToRoute: Constant(false),
});

export enum NavigationKeys {
  Home = 'home',
  PointOfSale = 'pointOfSale',
  Surveys = 'surveys',
  Settings = 'settings',
  Dashboard = 'dashboards',
  Scheduling = 'scheduling',
  Orders = 'orders',
  Customers = 'customers',
  Stock = 'stock',
  Fleet = 'fleet',
  Locations = 'locations',
  Discounts = 'discounts',
  Users = 'users',
  RedactData = 'redact-data',
  StoreAccounting = 'store-accounting',
}

export const NavigationKeysExceptionList = [
  NavigationKeys.Home,
  NavigationKeys.PointOfSale,
];

export const useNavigationPermissionContext = () =>
  useContext(NavigationPermissionContext);

export const NavigationPermissionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    data: navigationPermissions,
    isLoading: isNavigationPermissionsLoading,
  } = trpc.navigation.getUserNavigationPermissions.useQuery(undefined, {
    cacheTime: time.oneHourInSecs,
    staleTime: time.oneHourInSecs,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { first: fleetEnabledSetting } = useSettingsByPath({
    path: 'Fleet>EnableFleet',
  });

  const hasAccessToRoute = useCallback(
    (route: string) => {
      if (route === NavigationKeys.Fleet) {
        return fleetEnabledSetting?.value !== 'true' ? false : true;
      }
      return (
        NavigationKeysExceptionList.includes(route as NavigationKeys) ||
        !!navigationPermissions?.find((elm) => route === elm.Name)?.Access
      );
    },
    [navigationPermissions, fleetEnabledSetting]
  );

  return (
    <NavigationPermissionContext.Provider
      value={{
        navigationPermissions,
        isNavigationPermissionsLoading,
        hasAccessToRoute,
      }}
    >
      {children}
    </NavigationPermissionContext.Provider>
  );
};
